/**
 * Inspection → Zoho, when the agent taps Next from the attic inspection to the quote.
 *
 *   findings → the Zoho Inspection's fields (depth, size, insulation type, soffits, air sealing,
 *              exhaust, mould, pest, inspector notes) · Stage → Waiting for Report/Quote
 *   photos   → the photos-only PDF (Zoho Writer), saved in the Inspection's Report PDF field
 *
 * Goes through n8n "[C] App 4 - Submit Inspection" (REACT_APP_INSPECTION_SUBMIT_URL).
 * Redoing the inspection and tapping Next again replaces the findings and the PDF — never duplicates.
 * No signal → queued; when it goes out it rebuilds from the latest saved draft, so it always sends
 * the newest version and uploads any photo that was still on the phone.
 */
import { LEADS_SOURCE } from './leads';
import { getDraft } from './drafts';
import { isUploaded, uploadPending } from './photoStorage';
import { getAccessToken } from './auth';
import { queueChange, registerZohoAction } from './sync';

export const SUBMIT_URL = process.env.REACT_APP_INSPECTION_SUBMIT_URL;
export const SUBMIT_LIVE = LEADS_SOURCE === 'zoho' && !!SUBMIT_URL;

/* ---------- findings → Zoho fields ---------- */
const num = (v) => {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const round1 = (n) => Math.round(n * 10) / 10;

const SOFFIT = { 'Soffits blocked': 'Soffit Blocked', 'Baffles present': 'Baffles Present', 'Baffles needed': 'Baffles Needed' };
const AIR = { 'Gaps around penetrations': 'Gaps Around Penetrations', 'Pot light covers needed': 'Pot Lights Cover Needed' };
const EXHAUST = { exterior: 'Vented to Exterior', attic: 'Vented to Attic', confirm: 'Could Not Confirm' };
const SEVERITY = { L1: 'Level 1', L2: 'Level 2' };

// Zoho has one insulation type: cellulose wins, then batts; loose fibreglass → "Fiberglass / Other" (decided)
const insulationType = (materials) => {
  if (materials.includes('cellulose')) return 'Cellulose / Blown';
  if (materials.includes('fiberglass batts')) return 'Fiberglass / Batt';
  if (materials.includes('fiberglass')) return 'Fiberglass / Other';
  return null;
};

export const zohoFindings = (atticData) => {
  const f = (atticData && atticData.formData) || {};
  const areas = ((atticData && atticData.atticAreas) || []).map((a) => ({
    size: num(a.atticSize),
    depth: num(a.existingDepth),
    materials: a.insulationMaterials || [],
  }));
  const sized = areas.filter((a) => a.size && a.depth != null);
  const totalSize = areas.reduce((s, a) => s + (a.size || 0), 0);
  // more than one area: depth averaged by area size
  const depth = sized.length
    ? sized.reduce((s, a) => s + a.depth * a.size, 0) / sized.reduce((s, a) => s + a.size, 0)
    : (areas.find((a) => a.depth != null) || {}).depth;
  const mould = f.moldDetected === 'yes';
  const notes = [f.inspectorNotes, f.pestEvidence === 'yes' && f.pestNotes && `Pest: ${f.pestNotes}`]
    .filter((s) => s && String(s).trim())
    .join('\n\n');

  // every field is always sent, so a redo clears what's no longer true
  return {
    Insulation_Depth_in: depth == null ? null : round1(depth),
    Attic_Size_sq_ft: totalSize ? round1(totalSize) : null,
    Insulation_Type: insulationType(areas.flatMap((a) => a.materials)),
    Soffit_Baffle_Findings: (f.soffitBaffleState || []).map((s) => SOFFIT[s]).filter(Boolean),
    Soffit_Notes: f.soffit12Notes || null,
    Air_Sealing_Findings: (f.airSealingIssues || []).map((s) => AIR[s]).filter(Boolean),
    Air_Sealing_Notes: f.airSealing13Notes || null,
    Exhaust_Venting: EXHAUST[f.bathFanTermination] || null,
    Mould_Present: mould ? 'Yes' : 'No',
    Mould_Severity: mould ? SEVERITY[f.moldSeverityLevel] || null : null,
    Area_Affected: mould ? f.moldCoverageArea || null : null,
    Pest_Evidence_Present: f.pestEvidence === 'yes' ? 'Yes' : 'No',
    Inspector_Notes: notes || null,
  };
};

/* ---------- photos, in the PDF's order and with its headings ---------- */
const PDF_SECTIONS = [
  ['overview', 'Attic Overview'],
  ['depth', 'Insulation Depth'],
  ['soffit', 'Soffits & Baffles'],
  ['airSealing', 'Air Sealing'],
  ['hatch', 'Attic Hatch'],
  ['exhaust', 'Exhaust Venting'],
  ['mold', 'Mould'],
  ['pest', 'Pest Evidence'],
];

export const pdfPhotos = (frontPhoto, atticData) => {
  const slots = (atticData && atticData.photos) || {};
  const f = (atticData && atticData.formData) || {};
  const out = frontPhoto ? [{ section: 'Front of the House', src: frontPhoto }] : [];
  PDF_SECTIONS.forEach(([prefix, section]) => {
    // mould / pest photos only count when the answer is still "yes" (a redo can switch it off)
    if (prefix === 'mold' && f.moldDetected !== 'yes') return;
    if (prefix === 'pest' && f.pestEvidence !== 'yes') return;
    Object.keys(slots)
      .filter((k) => k === prefix || k.startsWith(`${prefix}-`))
      .sort()
      .forEach((k) => (slots[k] || []).forEach((src) => out.push({ section, src })));
  });
  return out;
};

/* ---------- sending ---------- */
const buildBody = async ({ inspectionId, draftId, frontPhoto, atticData }) => {
  const photos = pdfPhotos(frontPhoto, atticData);
  const { list, pending } = await uploadPending(photos.map((p) => p.src));
  return {
    pending,
    body: {
      action: 'submit',
      inspectionId,
      draftId,
      findings: zohoFindings(atticData),
      photos: photos.map((p, i) => ({ section: p.section, url: list[i] })).filter((p) => isUploaded(p.url)),
    },
  };
};

const noSignal = (message) => Object.assign(new Error(message), { offline: true });

/* Post one submit to n8n. Same error contract as sendInspectionUpdate: err.offline → retry later. */
export const sendSubmit = async (body, getToken) => {
  const token = await getToken();
  if (!token) throw new Error('Sign in again to save the inspection to Zoho.');
  let res;
  try {
    res = await fetch(SUBMIT_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw noSignal('No connection — the inspection will be sent to Zoho when you’re back online.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) {
    const err = new Error(out.error || `Zoho didn’t save the inspection (${res.status}).`);
    err.offline = res.status >= 500;
    throw err;
  }
  return out;
};

/* The queue calls this when a waiting submit goes out: rebuild from the newest draft, upload, send. */
export const pushQueuedSubmit = async (zoho, getToken) => {
  const draft = zoho.draftId ? await getDraft(zoho.draftId) : null;
  let body = zoho;
  if (draft) {
    const built = await buildBody({ inspectionId: zoho.inspectionId, draftId: zoho.draftId, frontPhoto: draft.frontPhoto, atticData: draft.atticData });
    if (built.pending) throw noSignal(`${built.pending} photo${built.pending === 1 ? '' : 's'} still on the phone — waiting for signal.`);
    body = built.body;
  }
  return sendSubmit(body, getToken);
};

registerZohoAction('submit', pushQueuedSubmit);

/**
 * Next → quote. Sends straight away when every photo is online; otherwise queues it (latest wins).
 * Never blocks the agent — resolves with { sent, queued } or { skipped } for walk-ins / test mode.
 */
export const submitInspection = async ({ inspectionId, draftId, customer, frontPhoto, atticData }) => {
  if (!SUBMIT_LIVE || !inspectionId) return { skipped: true };
  const label = `${customer || 'Customer'} — inspection saved to Zoho`;
  const { body, pending } = await buildBody({ inspectionId, draftId, frontPhoto, atticData });
  // queued copy keeps online links only — photos still on the phone are re-read from the draft later
  const queue = () => {
    queueChange({ type: 'inspection.submit', leadId: inspectionId, label, payload: { photos: body.photos.length }, zoho: body, supersede: `submit:${inspectionId}` });
    return { queued: true };
  };
  if (pending) return queue();
  try {
    const saved = await sendSubmit(body, getAccessToken);
    queueChange({ type: 'inspection.submit', leadId: inspectionId, label, payload: { photos: body.photos.length }, zoho: body, synced: true, supersede: `submit:${inspectionId}` });
    return { sent: true, saved };
  } catch (err) {
    if (!err.offline) throw err;
    return queue();
  }
};
